import React, { useState, useEffect } from 'react';
import {
  Workflow, Plus, Search, Play, Pause, Trash2, CheckCircle, Clock,
  Zap, MoreVertical, ArrowRight, Mail, Bell, Users, Target, Edit2,
  Settings2, X, GripVertical, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const statusColors = {
  draft: 'bg-slate-500',
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  archived: 'bg-gray-500',
};

const triggerTypes = [
  { value: 'form_submitted', label: 'Form Submitted', icon: Mail, description: 'When a form is submitted on a landing page' },
  { value: 'qr_scanned', label: 'QR Code Scanned', icon: Target, description: 'When a QR code is scanned' },
  { value: 'lead_created', label: 'Lead Created', icon: Users, description: 'When a new lead is created' },
  { value: 'page_visited', label: 'Page Visited', icon: Zap, description: 'When a landing page is visited' },
  { value: 'url_clicked', label: 'URL Clicked', icon: Zap, description: 'When a short URL is clicked' },
];

const actionTypes = [
  { value: 'send_email', label: 'Send Email', icon: Mail, description: 'Send an email notification' },
  { value: 'send_notification', label: 'Send Notification', icon: Bell, description: 'Send an in-app notification' },
  { value: 'create_lead', label: 'Create Lead', icon: Users, description: 'Create a new lead record' },
  { value: 'trigger_webhook', label: 'Trigger Webhook', icon: Zap, description: 'Call an external webhook URL' },
  { value: 'update_lead', label: 'Update Lead', icon: Edit2, description: 'Update an existing lead' },
  { value: 'assign_lead', label: 'Assign Lead', icon: Users, description: 'Assign lead to a user' },
];

const triggerIcons = {
  form_submitted: Mail,
  qr_scanned: Target,
  lead_created: Users,
  page_visited: Zap,
  url_clicked: Zap,
};

const actionIcons = {
  send_email: Mail,
  send_notification: Bell,
  create_lead: Users,
  trigger_webhook: Zap,
  update_lead: Edit2,
  assign_lead: Users,
};

export default function Workflows() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  
  // Form state
  const [workflowForm, setWorkflowForm] = useState({
    name: '',
    description: '',
    trigger: { type: 'form_submitted', conditions: {} },
    actions: [],
  });

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      // apiV1 attaches the Bearer token; the IDOR fix elsewhere in
      // this PR makes the workflows endpoints reject anon callers.
      const response = await apiV1.get('/workflows/');
      if (response.data?.success) {
        setWorkflows(response.data.data || []);
      }
    } catch (error) {
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingWorkflow(null);
    setWorkflowForm({
      name: '',
      description: '',
      trigger: { type: 'form_submitted', conditions: {} },
      actions: [],
    });
    setActiveTab('details');
    setShowWorkflowDialog(true);
  };

  const openEditDialog = (workflow) => {
    setEditingWorkflow(workflow);
    setWorkflowForm({
      name: workflow.name,
      description: workflow.description || '',
      trigger: workflow.trigger || { type: 'form_submitted', conditions: {} },
      actions: workflow.actions || [],
    });
    setActiveTab('details');
    setShowWorkflowDialog(true);
  };

  // Validation helpers
  const isDetailsComplete = () => {
    return workflowForm.name.trim().length > 0;
  };

  const isTriggerComplete = () => {
    return workflowForm.trigger && workflowForm.trigger.type;
  };

  const isActionsComplete = () => {
    return workflowForm.actions.length > 0;
  };

  const getValidationErrors = () => {
    const errors = [];
    if (!isDetailsComplete()) {
      errors.push({ tab: 'details', message: 'Workflow name is required' });
    }
    if (!isTriggerComplete()) {
      errors.push({ tab: 'trigger', message: 'Please select a trigger' });
    }
    if (!isActionsComplete()) {
      errors.push({ tab: 'actions', message: 'At least one action is required' });
    }
    return errors;
  };

  const isFormValid = () => {
    return isDetailsComplete() && isTriggerComplete() && isActionsComplete();
  };

  const handleSave = async () => {
    const errors = getValidationErrors();
    
    if (errors.length > 0) {
      // Show all validation errors
      errors.forEach(err => toast.error(err.message));
      
      // Navigate to the first incomplete tab
      setActiveTab(errors[0].tab);
      return;
    }

    try {
      const payload = {
        name: workflowForm.name,
        description: workflowForm.description || null,
        trigger: workflowForm.trigger,
        actions: workflowForm.actions,
      };

      const response = editingWorkflow
        ? await apiV1.put(`/workflows/${editingWorkflow.id}`, payload)
        : await apiV1.post('/workflows/', payload);

      if (response.data?.success) {
        toast.success(editingWorkflow ? 'Workflow updated!' : 'Workflow created!');
        setShowWorkflowDialog(false);
        loadWorkflows();
      } else {
        toast.error(response.data?.detail || 'Failed to save workflow');
      }
    } catch (error) {
      toast.error('Failed to save workflow');
    }
  };

  const handleActivate = async (id) => {
    try {
      const response = await apiV1.post(`/workflows/${id}/activate`);
      if (response.data?.success) {
        toast.success('Workflow activated!');
        loadWorkflows();
      }
    } catch (error) {
      toast.error('Failed to activate workflow');
    }
  };

  const handlePause = async (id) => {
    try {
      const response = await apiV1.post(`/workflows/${id}/pause`);
      if (response.data?.success) {
        toast.success('Workflow paused');
        loadWorkflows();
      }
    } catch (error) {
      toast.error('Failed to pause workflow');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    try {
      const response = await apiV1.delete(`/workflows/${id}`);
      if (response.data?.success) {
        toast.success('Workflow deleted');
        loadWorkflows();
      }
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  // Action management
  const addAction = () => {
    setWorkflowForm({
      ...workflowForm,
      actions: [
        ...workflowForm.actions,
        { type: 'send_email', config: {}, order: workflowForm.actions.length },
      ],
    });
  };

  const updateAction = (index, updates) => {
    const newActions = [...workflowForm.actions];
    newActions[index] = { ...newActions[index], ...updates };
    setWorkflowForm({ ...workflowForm, actions: newActions });
  };

  const removeAction = (index) => {
    const newActions = workflowForm.actions.filter((_, i) => i !== index);
    // Re-order
    newActions.forEach((a, i) => a.order = i);
    setWorkflowForm({ ...workflowForm, actions: newActions });
  };

  const moveAction = (index, direction) => {
    const newActions = [...workflowForm.actions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newActions.length) return;
    
    [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];
    newActions.forEach((a, i) => a.order = i);
    setWorkflowForm({ ...workflowForm, actions: newActions });
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalExecutions = workflows.reduce((sum, w) => sum + (w.total_runs || 0), 0);

  // Render action config form based on type
  const renderActionConfig = (action, index) => {
    switch (action.type) {
      case 'send_email':
        return (
          <div className="space-y-3 mt-3 pl-4 border-l-2 border-muted">
            <div className="space-y-1">
              <Label className="text-xs">To Email</Label>
              <Input
                placeholder="{{lead.email}} or specific@email.com"
                value={action.config?.to_email || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, to_email: e.target.value } })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input
                placeholder="Email subject"
                value={action.config?.subject || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, subject: e.target.value } })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Template ID (optional)</Label>
              <Input
                placeholder="template_id"
                value={action.config?.template_id || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, template_id: e.target.value } })}
                className="h-8 text-sm"
              />
            </div>
          </div>
        );
      case 'trigger_webhook':
        return (
          <div className="space-y-3 mt-3 pl-4 border-l-2 border-muted">
            <div className="space-y-1">
              <Label className="text-xs">Webhook URL</Label>
              <Input
                placeholder="https://api.example.com/webhook"
                value={action.config?.url || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, url: e.target.value } })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">HTTP Method</Label>
              <Select
                value={action.config?.method || 'POST'}
                onValueChange={(v) => updateAction(index, { config: { ...action.config, method: v } })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'send_notification':
        return (
          <div className="space-y-3 mt-3 pl-4 border-l-2 border-muted">
            <div className="space-y-1">
              <Label className="text-xs">Notification Title</Label>
              <Input
                placeholder="New Lead!"
                value={action.config?.title || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, title: e.target.value } })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message</Label>
              <Textarea
                placeholder="A new lead has been created: {{lead.name}}"
                value={action.config?.message || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, message: e.target.value } })}
                rows={2}
                className="text-sm"
              />
            </div>
          </div>
        );
      case 'assign_lead':
        return (
          <div className="space-y-3 mt-3 pl-4 border-l-2 border-muted">
            <div className="space-y-1">
              <Label className="text-xs">Assign To (User ID or email)</Label>
              <Input
                placeholder="user@example.com"
                value={action.config?.assign_to || ''}
                onChange={(e) => updateAction(index, { config: { ...action.config, assign_to: e.target.value } })}
                className="h-8 text-sm"
              />
            </div>
          </div>
        );
      default:
        return (
          <div className="mt-3 pl-4 border-l-2 border-muted">
            <p className="text-xs text-muted-foreground">No additional configuration needed</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6" data-testid="workflows-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Workflows</h1>
          <p className="text-muted-foreground mt-1">Automate actions based on triggers</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-workflow-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Workflow
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Workflows</p>
                <p className="text-2xl font-bold">{workflows.length}</p>
              </div>
              <Workflow className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {workflows.filter(w => w.status === 'active').length}
                </p>
              </div>
              <Play className="w-8 h-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Executions</p>
                <p className="text-2xl font-bold text-blue-600">{totalExecutions}</p>
              </div>
              <Zap className="w-8 h-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">With Actions</p>
                <p className="text-2xl font-bold text-purple-600">
                  {workflows.filter(w => w.actions?.length > 0).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-workflows"
        />
      </div>

      {/* Workflows List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-muted rounded w-1/3 mb-4" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Workflow className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No workflows yet</h3>
            <p className="text-muted-foreground mb-4">Create your first workflow to automate tasks</p>
            <Button onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Create Workflow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="workflows-list">
          {filteredWorkflows.map(workflow => {
            const triggerType = workflow.trigger?.type || workflow.trigger_type;
            const TriggerIcon = triggerIcons[triggerType] || Zap;
            return (
              <Card key={workflow.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{workflow.name}</h3>
                        <Badge className={`${statusColors[workflow.status]} text-white text-xs`}>
                          {workflow.status}
                        </Badge>
                        {(!workflow.actions || workflow.actions.length === 0) && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No actions
                          </Badge>
                        )}
                      </div>
                      {workflow.description && (
                        <p className="text-sm text-muted-foreground mb-4">{workflow.description}</p>
                      )}

                      {/* Trigger -> Actions visualization */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <TriggerIcon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                            {triggerType?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {workflow.actions && workflow.actions.length > 0 && (
                          <>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                            {workflow.actions.map((action, idx) => {
                              const ActionIcon = actionIcons[action.type] || Zap;
                              return (
                                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                                  <ActionIcon className="w-4 h-4 text-emerald-600" />
                                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                                    {action.type?.replace(/_/g, ' ')}
                                  </span>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Zap className="w-4 h-4" />
                          {workflow.total_runs || 0} executions
                        </span>
                        {workflow.last_run_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Last: {new Date(workflow.last_run_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`workflow-menu-${workflow.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(workflow)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit Workflow
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {workflow.status !== 'active' ? (
                          <DropdownMenuItem onClick={() => handleActivate(workflow.id)}>
                            <Play className="w-4 h-4 mr-2" />
                            Activate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handlePause(workflow.id)}>
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(workflow.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Workflow Dialog */}
      <Dialog open={showWorkflowDialog} onOpenChange={setShowWorkflowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
            </DialogTitle>
            <DialogDescription>
              Configure your workflow trigger and actions
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details" className="relative">
                <Settings2 className="w-4 h-4 mr-2" />
                Details
                {isDetailsComplete() ? (
                  <CheckCircle className="w-4 h-4 ml-2 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 ml-2 text-amber-500" />
                )}
              </TabsTrigger>
              <TabsTrigger value="trigger" className="relative">
                <Target className="w-4 h-4 mr-2" />
                Trigger
                {isTriggerComplete() ? (
                  <CheckCircle className="w-4 h-4 ml-2 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 ml-2 text-amber-500" />
                )}
              </TabsTrigger>
              <TabsTrigger value="actions" className="relative">
                <Zap className="w-4 h-4 mr-2" />
                Actions ({workflowForm.actions.length})
                {isActionsComplete() ? (
                  <CheckCircle className="w-4 h-4 ml-2 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 ml-2 text-amber-500" />
                )}
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 pr-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Workflow Name *</Label>
                  <Input
                    id="name"
                    placeholder="New Lead Notification"
                    value={workflowForm.name}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                    data-testid="workflow-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="What does this workflow do?"
                    value={workflowForm.description}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </TabsContent>

              {/* Trigger Tab */}
              <TabsContent value="trigger" className="space-y-4 pr-4">
                <div className="space-y-2">
                  <Label>When this happens...</Label>
                  <div className="grid gap-3">
                    {triggerTypes.map((trigger) => {
                      const Icon = trigger.icon;
                      const isSelected = workflowForm.trigger.type === trigger.value;
                      return (
                        <div
                          key={trigger.value}
                          onClick={() => setWorkflowForm({
                            ...workflowForm,
                            trigger: { ...workflowForm.trigger, type: trigger.value }
                          })}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-muted hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
                              <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                            </div>
                            <div>
                              <p className="font-medium">{trigger.label}</p>
                              <p className="text-sm text-muted-foreground">{trigger.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Trigger conditions (future enhancement) */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Advanced trigger conditions coming soon
                  </p>
                </div>
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="space-y-4 pr-4">
                <div className="flex items-center justify-between">
                  <Label>Then do these actions...</Label>
                  <Button size="sm" variant="outline" onClick={addAction}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Action
                  </Button>
                </div>

                {workflowForm.actions.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <Zap className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No actions yet</p>
                    <Button size="sm" variant="outline" className="mt-2" onClick={addAction}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Action
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {workflowForm.actions.map((action, index) => {
                      const ActionIcon = actionIcons[action.type] || Zap;
                      const actionDef = actionTypes.find(a => a.value === action.type);
                      return (
                        <Card key={index} className="relative">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="flex flex-col items-center gap-1 pt-1">
                                <span className="text-xs text-muted-foreground font-medium">
                                  #{index + 1}
                                </span>
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Select
                                    value={action.type}
                                    onValueChange={(v) => updateAction(index, { type: v, config: {} })}
                                  >
                                    <SelectTrigger className="w-[200px]">
                                      <div className="flex items-center gap-2">
                                        <ActionIcon className="w-4 h-4" />
                                        <SelectValue />
                                      </div>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {actionTypes.map((at) => {
                                        const AtIcon = at.icon;
                                        return (
                                          <SelectItem key={at.value} value={at.value}>
                                            <div className="flex items-center gap-2">
                                              <AtIcon className="w-4 h-4" />
                                              {at.label}
                                            </div>
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-sm text-muted-foreground flex-1">
                                    {actionDef?.description}
                                  </p>
                                </div>
                                
                                {/* Action-specific config */}
                                {renderActionConfig(action, index)}
                              </div>

                              <div className="flex flex-col gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => moveAction(index, 'up')}
                                  disabled={index === 0}
                                >
                                  <GripVertical className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => removeAction(index)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Available variables:</strong> Use {'{{lead.name}}'}, {'{{lead.email}}'}, {'{{form.field_name}}'} in your action configurations.
                  </p>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          {/* Validation Summary */}
          {!isFormValid() && (
            <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Complete all steps to save:
              </p>
              <ul className="mt-1 text-sm text-amber-700 dark:text-amber-400 space-y-1 ml-6">
                {!isDetailsComplete() && <li>• Add a workflow name</li>}
                {!isTriggerComplete() && <li>• Select a trigger</li>}
                {!isActionsComplete() && <li>• Add at least one action</li>}
              </ul>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowWorkflowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!isFormValid()}
              data-testid="save-workflow-btn"
            >
              {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
